// definition-store.ts — Janus C1: the page's memory-only definition store.
//
// Misses use one ≤64-id batch. Native adoption closes/finalizes its split catalog before interning;
// nested tokens belong to the exact dependency revisions in that carrier. Saved target gates and
// account scrubbing stay on the server; game content travels intact.
import { compileFetchedEntity } from '../../shared/compile-fetched-entity';
import { previewSpawnDraft } from '../../shared/spawn-browser-bridge';
import { readItemField } from '../compile/item-field-reader';
import { objectRequest } from '../../shared/object-request';

type Row = Record<string, unknown>;
const isObj = (v: unknown): v is Row => v !== null && typeof v === 'object' && !Array.isArray(v);
const upper = (v: unknown): string => typeof v === 'string' ? v.toUpperCase() : '';
const validUuid = (v: string): boolean => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);
export const DEFINITION_FETCH_TIMEOUT_MS = 15000;
const definitionEnvelope = (row: Row): Row | null => isObj(row._entity_base) ? row._entity_base : null;
function definitionInputRevisions(row: Row): Row | null {
    const base = definitionEnvelope(row)?.base;
    return isObj(row._definition_input_revs) ? row._definition_input_revs : isObj(base) && isObj(base.dependency_revs) ? base.dependency_revs : null;
}
function applySavedPl(adopted: Row, savedPl: Row | null): Row {
    if (!savedPl) return adopted;
    Object.defineProperty(adopted, '_pl_stash', { value: savedPl, enumerable: false, writable: true, configurable: true });
    const computed: Row = { ...isObj(adopted.computed) ? adopted.computed : {} };
    adopted.computed = computed;
    delete computed.pl; delete computed.pl_meta; delete computed.status_effect_permanent_pl;
    if (savedPl.verdict === 'hit' && typeof savedPl.pl === 'number') {
        computed.pl = savedPl.pl; computed.pl_meta = savedPl.meta;
        if (isObj(savedPl.sep)) computed.status_effect_permanent_pl = savedPl.sep;
        delete computed.pl_pending;
    }
    return adopted;
}

/** Stateless adoption shared by generic single reads and batches; the page owns the compiler. */
export async function adoptFetchedObject(source: Row): Promise<Row> {
    const savedPl = isObj(source._pl_stash) ? source._pl_stash : null;
    const tableRanges = Array.isArray(source.random_table_ranges) ? source.random_table_ranges : null;
    const tableLayers = tableRanges && isObj(source.item_layer_types) ? source.item_layer_types : null;
    const definition = isObj(source._entity_base) && source._entity_base.entity === 'definition';
    let adopted: Row;
    if (definition && readItemField(source, 'item_type') === 'creature') {
        const preview = await previewSpawnDraft(String(readItemField(source, 'item_id')));
        adopted = { ...source, ...preview };
        Object.defineProperty(adopted, '_definition_input_revs', { value: preview._definition_input_revs });
        delete adopted._entity_base; delete adopted._entity_refusal;
    } else if (isObj(source.inventory) && source._inventory_base && !source._entity_base) {
        // The inventory reader deliberately returns an instance SELECT shell beside its tree
        // facet. Preserve that live-row contract, including a pending facet, for row consumers.
        const result = await window.ftAdoptInventoryBase?.(source, { detached: true });
        adopted = isObj(result) ? result : source;
    } else adopted = await compileFetchedEntity(source);
    const finalized = isObj(adopted.computed) && isObj(adopted.computed._cache) ? adopted.computed._cache : null;
    if (definition && typeof finalized?.pl_sig !== 'string') throw new Error('entity-fetch:definition-not-finalized');
    if (definition) Object.defineProperty(adopted, '_definition_engine', { value: definitionEnvelope(source)?.engine, configurable: true });
    if (tableRanges) {
        adopted.random_table_ranges = tableRanges;
        if (tableLayers) adopted.item_layer_types = { ...tableLayers, ...isObj(adopted.item_layer_types) ? adopted.item_layer_types : {} };
    }
    return applySavedPl(adopted, savedPl);
}

export interface StoredDefinition { id: string; def: Row; revision_token: string | null; }
export interface DefinitionStoreStats { hits: number; misses: number; fetches: number; interned: number; drops: number; unresolved: number; }

export class DefinitionStore {
    private readonly entries = new Map<string, StoredDefinition>();
    private readonly refused = new Map<string, 'missing' | 'forbidden'>();
    /** 2026-09-24 late: id → the batch fetch already carrying it. A second caller (a claim and a background prewarm) awaits that one
     *  instead of issuing a duplicate request for the same definition (measured: the same status fetched twice, 0.8 s apart). */
    private readonly inflight = new Map<string, Promise<void>>();
    private epochValue: number | null | undefined = undefined;
    /** N4c (2026-10-02, coordinator Codex round 2 §3a): bumped by every clear(). A request issued under an older generation never interns
     *  (its answer may predate the boundary the clear marked) — it is re-asked once under the current one. */
    private generation = 0;
    private readonly clearListeners = new Set<() => void>();
    onClear(listener: () => void): void { this.clearListeners.add(listener); }
    /** N4d (2026-10-02, Codex Round 9 — same-epoch answer ordering): every batch request takes the next sequence number. Per id, an answer
     *  is applied only when its request is NEWER than the last one applied for that id — so an older refusal landing after a forced refresh
     *  cannot evict it (nor an older success overwrite a newer refusal) when access changes without an epoch move. */
    private seq = 0;
    private readonly appliedSeq = new Map<string, number>();
    /** id → the sequence of the request whose TOP-LEVEL answer last interned it (a forced caller is satisfied only by its own era's). */
    private readonly topLevelSeq = new Map<string, number>();
    readonly stats: DefinitionStoreStats = { hits: 0, misses: 0, fetches: 0, interned: 0, drops: 0, unresolved: 0 };
    constructor(private readonly baseUrl: string, private readonly fetchImpl: typeof fetch = (...args) => fetch(...args)) {}

    /** The last epoch seen on the wire (undefined until the first batch answers). */
    get epoch(): number | null | undefined { return this.epochValue; }
    /** Page-local derived reads must also expire after an explicit clear at the same epoch. */
    get cacheGeneration(): number { return this.generation; }
    /** Distinguish a top-level response from a nested catalog copy with sparse computed fields elided. */
    isTopLevel(id: string): boolean { return this.topLevelSeq.has(upper(id)); }
    get size(): number { return this.entries.size; }
    /** A list heal may wait once for the closure its expired claim is already fetching.
     * Action callers retain their original deadline. Only arrived definitions count as progress. */
    async settlePending(timeoutMs: number, since = this.stats.interned): Promise<boolean> {
        const pending = [...new Set(this.inflight.values())];
        if (!pending.length) return this.stats.interned > since;
        let timer: ReturnType<typeof setTimeout> | undefined;
        try {
            await Promise.race([Promise.allSettled(pending), new Promise<void>(resolve => { timer = setTimeout(resolve, timeoutMs); })]);
        } finally { clearTimeout(timer); }
        return this.stats.interned > since;
    }
    peek(id: string): StoredDefinition | null { return this.entries.get(upper(id)) ?? null; }
    refusal(id: string): 'missing' | 'forbidden' | null { return this.refused.get(upper(id)) ?? null; }
    /** Sync reader for the compile entry: the definition or null (a miss the caller resolves through getDefinitions first). */
    definition(id: string): Row | null { const e = this.entries.get(upper(id)); if (e) this.stats.hits += 1; else this.stats.misses += 1; return e?.def ?? null; }
    /** Drops every entry and refusal AND every in-flight registration: a later caller never joins a request issued before the clear, and
     *  such a request's answer is not interned (Codex round 2 §3a: a joined pre-clear request repopulated the cleared store). */
    clear(): void { this.entries.clear(); this.refused.clear(); this.inflight.clear(); this.appliedSeq.clear(); this.topLevelSeq.clear(); this.generation += 1; this.stats.drops += 1; for (const listener of this.clearListeners) listener(); }
    /** C1k: an epoch observed off the wire outside a batch answer (a move receipt) — same forward-only rule. */
    noteEpoch(epoch: unknown): void { this.observeEpoch(epoch); }

    private observeEpoch(epoch: unknown): void {
        const value = typeof epoch === 'number' && Number.isFinite(epoch) ? epoch : null;
        // FORWARD ONLY (C2b step-2 review 1): an out-of-order older answer, or a transient null (a Redis read blip), neither drops the
        // store nor moves the epoch backwards; only a strictly newer epoch drops it.
        if (value === null) { if (this.epochValue === undefined) this.epochValue = null; return; }
        if (typeof this.epochValue === 'number' && value < this.epochValue) return;
        if (typeof this.epochValue === 'number' && value > this.epochValue) this.clear();
        // N4c (Codex round 2 §3a): the FIRST numeric epoch after an unknown era drops what that era held — an entry (or refusal) interned
        // while the epoch was unknown cannot be placed before or after the saves the number now accounts for
        else if (typeof this.epochValue !== 'number' && (this.entries.size || this.refused.size)) this.clear();
        this.epochValue = value;
    }
    private intern(id: string, def: Row, tokens: Row, seq: number, tokenRevisions?: Row): void {
        const key = upper(id);
        const token = tokens[key];
        this.entries.set(key, { id: key, def, revision_token: typeof token === 'string' ? token : null });
        this.refused.delete(key);
        this.topLevelSeq.set(key, seq);
        this.stats.interned += 1;
        for (const [cid, cdef] of Object.entries(isObj(def.catalog) ? def.catalog : {})) {
            if (!isObj(cdef) || !validUuid(cid)) continue;
            const ckey = upper(cid);
            const envelope = isObj(def._entity_base) ? def._entity_base : isObj(def._inventory_base) ? def._inventory_base : null;
            const base = envelope && isObj(envelope.base) ? envelope.base : null;
            const revisions = isObj(def._definition_input_revs) ? def._definition_input_revs
                : base && isObj(base.dependency_revs) ? base.dependency_revs : null;
            const computed = isObj(def.computed) ? def.computed : null;
            // Native catalog interning deliberately removes each child's computed defaults.
            // Certify the owning finalization, then accept its full layer nodes, never identity shells.
            if (!cdef.item_layer_types || (tokenRevisions && (!revisions || typeof revisions[ckey] !== 'number'
                || !computed || !isObj(computed._cache) || typeof computed._cache.pl_sig !== 'string'))) continue;
            // never over a held entry, and never over a REFUSAL (Codex round 2 §3a: a nested copy resurrected a refused id while its
            // refusal stayed recorded) — a refused id comes back only through its own top-level answer
            if (this.entries.has(ckey) || this.refused.has(ckey)) continue;
            const ctoken = !tokenRevisions || tokenRevisions[ckey] === revisions?.[ckey] ? tokens[ckey] : null;
            this.entries.set(ckey, { id: ckey, def: cdef, revision_token: typeof ctoken === 'string' ? ctoken : null });
            // (N4d, Codex) a nested install is ordered too: an OLDER top-level answer landing later must not overwrite it
            if ((this.appliedSeq.get(ckey) ?? 0) < seq) this.appliedSeq.set(ckey, seq);
            this.stats.interned += 1;
        }
    }

    /** Resolve every id (memory first, one batch fetch for the rest). Refused ids are absent from the map; ask refusal().
     * `force` fetches the ids TOP-LEVEL even when memory holds them — a copy interned from another payload's nested catalog is a
     * FULL definition without its sparse computed defaults (no stored PL); the top-level payload carries them and overwrites (C2). */
    async getDefinitions(ids: readonly string[], force = false): Promise<Map<string, StoredDefinition>> {
        const wanted: string[] = [];
        const missing: string[] = [];
        const joined: Promise<void>[] = [];
        // (N4d, Codex Round 9 P2) a FORCED caller is satisfied only by a top-level answer to a request issued after it started — never by a
        // nested catalog copy (a full definition without its sparse computed defaults) or an older answer
        const startSeq = this.seq;
        const resolved = (k: string): boolean => this.refused.has(k) ? (!force || (this.appliedSeq.get(k) ?? 0) > startSeq)
            : this.entries.has(k) && (!force || (this.topLevelSeq.get(k) ?? 0) > startSeq);
        for (const raw of ids) {
            const key = upper(raw);
            if (!validUuid(key) || wanted.includes(key)) continue;
            wanted.push(key);
            if (!force && this.entries.has(key)) continue;
            // a remembered refusal answers a plain ask; a FORCED ask re-asks the server (Codex round 2 §3a: access can change without an
            // epoch move — the refusal blocked every refresh until a clear)
            if (!force && this.refused.has(key)) continue;
            // Only a NON-forced caller (it needs the definition to EXIST) joins a request already carrying the id. A forced caller
            // needs an answer from AFTER its own invalidation boundary, so it always issues its own request (Codex review 2026-09-24:
            // a forced reseed joining an older prewarm installed a pre-mutation catalog; a failed joined refresh returned stale data).
            const running = force ? undefined : this.inflight.get(key);
            if (running) joined.push(running); else missing.push(key);
        }
        const own: Promise<void>[] = [];
        const batchSize = 64;
        for (let i = 0; i < missing.length; i += batchSize) {
            const chunk = missing.slice(i, i + batchSize);
            const run = this.fetchChunk(chunk, true);
            for (const key of chunk) this.inflight.set(key, run);
            own.push(run.finally(() => { for (const key of chunk) if (this.inflight.get(key) === run) this.inflight.delete(key); }));
        }
        // an owned batch's failure propagates to its caller exactly as before; a joined batch's failure leaves the id unresolved
        await Promise.all([...own, ...joined.map(p => p.catch(() => undefined))]);
        // RECONCILIATION (Codex review): an id this call treated as cached — or fetched itself, then saw evicted by a later
        // chunk's newer epoch (N4c, Codex round 2 §3a) — can be cleared meanwhile: request every unresolved id once, in ≤ 64-id chunks
        // (the old pass took only the first 64), so every requested id resolves unless the server refuses it
        // (N4c Codex review) BOUNDED passes: a later chunk's newer epoch can clear what an earlier chunk of the same pass interned, so the
        // unresolved set is recomputed after every pass (≤ 3). Ids still unresolved after the budget are counted (stats.unresolved) and
        // left absent — the caller's miss path (def-missing → retry) handles them; never an endless refetch under epoch churn
        for (let pass = 0; pass < 3; pass++) {
            const cleared = wanted.filter(k => !resolved(k));
            if (!cleared.length) break;
            const retrySize = 64;
            for (let i = 0; i < cleared.length; i += retrySize) await this.fetchChunk(cleared.slice(i, i + retrySize), true).catch(() => undefined);
        }
        const unresolved = wanted.filter(k => !resolved(k)).length;
        if (unresolved) this.stats.unresolved += unresolved;
        // assembled from the CURRENT store: an entry a newer epoch cleared during this call is not returned (Codex review)
        const out = new Map<string, StoredDefinition>();
        // (N4d, Codex) a FORCED caller gets only what satisfied it (a top-level answer from after it started) — never a nested copy it rejected
        for (const key of wanted) { const e = this.entries.get(key); if (e && (!force || resolved(key))) out.set(key, e); }
        return out;
    }

    /** One POST of ≤ 64 ids; interns the answer. An answer built at an OLDER epoch than the store now holds (a newer answer cleared
     *  the store while this one was in flight) is discarded, never interned over the newer entries — and re-requested ONCE, so the
     *  ids resolve at the current epoch instead of staying missing. The bounded transport ceiling covers the body. */
    private async fetchChunk(chunk: string[], retryDiscarded: boolean, splitTimeout = true): Promise<void> {
        this.stats.fetches += 1;
        const issuedAt = this.generation;
        const mySeq = ++this.seq;
        // A 15 s ceiling: valid reads have hit the old 8 s abort under load. A hung batch must not pin a caller —
        // held through the BODY read too (a stalled body kept the in-flight entries pinned for every joiner)
        const ctl = typeof AbortController === 'function' ? new AbortController() : null;
        let payload: unknown;
        try {
            payload = await objectRequest(async () => {
                // Queue time is not transport time. Hold the slot through the body, release before compilation/children.
                const timer = ctl ? setTimeout(() => ctl.abort(), DEFINITION_FETCH_TIMEOUT_MS) : null;
                try {
                    const response = await this.fetchImpl(`${this.baseUrl}options/api/object_inc.php`, {
                        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ids: chunk }), credentials: 'same-origin', ...(ctl ? { signal: ctl.signal } : {}),
                    });
                    if (!response.ok) throw new Error(`object_inc batch returned ${response.status}`);
                    return await response.json();
                } finally { if (timer) clearTimeout(timer); }
            });
        } catch (error) {
            if (!ctl?.signal.aborted || chunk.length <= 1 || !splitTimeout) throw error;
            // Split this chunk ONCE. Future batches still use 64; children cannot split/retry a timeout.
            const childSize = Math.min(4, Math.ceil(chunk.length / 2));
            for (let i = 0; i < chunk.length; i += childSize * 2) {
                await Promise.all([chunk.slice(i, i + childSize), chunk.slice(i + childSize, i + childSize * 2)].filter(ids => ids.length).map(ids => this.fetchChunk(ids, retryDiscarded, false)));
            }
            return;
        }
        if (!isObj(payload) || payload.schema !== 'ft.object-batch.v1' || !isObj(payload.objects)) throw new TypeError('object_inc batch: bad payload');
        // discarded, never interned (re-asked once): an answer at an OLDER epoch than the store holds; an answer with NO epoch while the
        // store holds a known one (Codex round 2 §3a: a Redis-blip null answer overwrote entries installed at a newer epoch — it cannot be
        // ordered against them); and an answer to a request issued before a clear() (its bytes may predate that boundary)
        const answerEpoch = typeof payload.epoch === 'number' && Number.isFinite(payload.epoch) ? payload.epoch : null;
        const stale = typeof this.epochValue === 'number' && (answerEpoch === null || answerEpoch < this.epochValue);
        if (stale || issuedAt !== this.generation) {
            // (N4d, Codex Round 9 P2) a discarded answer still carries epoch EVIDENCE: a newer epoch drops what the store holds now
            if (answerEpoch !== null && (typeof this.epochValue !== 'number' || answerEpoch > this.epochValue)) this.observeEpoch(answerEpoch);
            if (retryDiscarded) await this.fetchChunk(chunk, false, splitTimeout);
            return;
        }
        this.observeEpoch(payload.epoch);
        const adoptingGeneration = this.generation;
        const answerCurrent = (): boolean => adoptingGeneration === this.generation
            && (typeof this.epochValue !== 'number' || this.epochValue === answerEpoch);
        const tokens = isObj(payload.revision_tokens) ? payload.revision_tokens : {};
        const tokenRevisions = isObj(payload.token_revisions) ? payload.token_revisions : {};
        const answered = new Set<string>(); // response keys, upper-cased once — the omitted-id pass below reads the same normalization
        const reusable = (key: string, obj: Row): StoredDefinition | undefined => {
            const held = this.entries.get(key), incoming = definitionEnvelope(obj);
            const oldRevs = held && definitionInputRevisions(held.def), newRevs = definitionInputRevisions(obj);
            // A forced read refreshes saved PL/access/tokens, but unchanged top-level
            // definitions need neither catalog hydration nor another native compile.
            return held && this.isTopLevel(key) && incoming?.entity === 'definition'
                && typeof answerEpoch === 'number' && typeof incoming.engine === 'string' && incoming.engine !== ''
                && held.def._definition_engine === incoming.engine
                && typeof tokens[key] === 'string' && held.revision_token === tokens[key]
                && oldRevs && newRevs && Object.keys(newRevs).length > 0
                && Object.entries(newRevs).every(([id, revision]) => oldRevs[id] === revision) ? held : undefined;
        };
        const objects = Object.entries(payload.objects);
        // Catalog I/O must not serialize behind the four-adoption limit: nine cold
        // definitions otherwise pay three network round trips before PL can continue.
        const cold = objects.filter(([id, obj]) => isObj(obj) && !obj.missing && !obj.forbidden
            && (this.appliedSeq.get(upper(id)) ?? 0) <= mySeq && !reusable(upper(id), obj)
            && readItemField(obj, 'item_type') !== 'creature').map(([, obj]) => obj);
        if (cold.length && typeof window !== 'undefined') await window.ftPreloadEntityCatalogs?.(cold);
        // Bound pending compiles; epoch/sequence checks still bracket each adoption.
        for (let i = 0; i < objects.length; i += 4) {
            if (!answerCurrent()) return;
            await Promise.all(objects.slice(i, i + 4).map(async ([id, obj]) => {
                const key = upper(id);
                if (!isObj(obj)) return;
                answered.add(key);
                // per id, only an answer to a NEWER request than the last one applied (N4d — same-epoch ordering)
                if ((this.appliedSeq.get(key) ?? 0) > mySeq) return;
                this.appliedSeq.set(key, mySeq);
                // a refusal EVICTS any cached copy (Codex review: a viewer-access change left the old definition readable)
                if (obj.missing === true) { this.refused.set(key, 'missing'); this.entries.delete(key); this.topLevelSeq.delete(key); return; }
                if (obj.forbidden === true) { this.refused.set(key, 'forbidden'); this.entries.delete(key); this.topLevelSeq.delete(key); return; }
                const rawEntity = isObj(obj._entity_base) || isObj(obj._inventory_base);
                let adopted: Row;
                const held = reusable(key, obj);
                try {
                    if (held) {
                        adopted = { ...held.def, permissions: obj.permissions, _meta: obj._meta };
                        for (const name of ['_definition_input_revs', '_definition_engine', '_entity_base']) {
                            const descriptor = Object.getOwnPropertyDescriptor(held.def, name);
                            if (descriptor) Object.defineProperty(adopted, name, descriptor);
                        }
                        applySavedPl(adopted, isObj(obj._pl_stash) ? obj._pl_stash : { verdict: 'missing' });
                    } else adopted = await adoptFetchedObject(obj);
                } catch { return; }
                // Entity compilation can yield for its catalog while another batch advances the store.
                if (!answerCurrent()) return;
                if ((this.appliedSeq.get(key) ?? 0) > mySeq) return;
                this.intern(key, adopted, tokens, mySeq, rawEntity ? tokenRevisions : undefined);
            }));
        }
        // N4b (2026-10-01, N4 ask 2): an id this batch asked for that the answer carries no usable object for (omitted, or a non-object
        // payload) is a refusal too — remembered like `missing`, so a claim stops refetching it every round (the cold-page def-missing
        // loop). A cached copy is evicted for the same reason a `missing` answer evicts it.
        if (!answerCurrent()) return;
        for (const raw of chunk) {
            const key = upper(raw);
            if (answered.has(key) || (this.appliedSeq.get(key) ?? 0) > mySeq) continue;
            // (N4d, Codex) the omission is APPLIED even over an existing refusal — its sequence must advance, or an older success lands after it
            this.appliedSeq.set(key, mySeq);
            if (this.refused.has(key)) continue;
            this.refused.set(key, 'missing'); this.entries.delete(key); this.topLevelSeq.delete(key);
        }
    }

    /** The `definitions` map the envelope carries (§8.3): every held id → its revision token. */
    tokensFor(ids: readonly string[]): Record<string, string> {
        const out: Record<string, string> = {};
        for (const raw of ids) { const e = this.entries.get(upper(raw)); if (e && e.revision_token) out[e.id] = e.revision_token; }
        return out;
    }
}

/** ONE store per PAGE (C2b step-2 review 5): this module is compiled into several bundles, so the instance lives on the global object —
 *  every bundle's C1 lane, PL claim and healer share the same entries, epoch and tokens. */
export function sharedDefinitionStore(baseUrl: string): DefinitionStore {
    const host = globalThis as unknown as { __ftDefinitionStore?: DefinitionStore };
    return host.__ftDefinitionStore ??= new DefinitionStore(baseUrl);
}
